import React from "react";
import { ToolTip } from "@truevault/aquilo";
import {
  RegionGroup,
  RegulationRequestTypeDto,
  WorkflowType,
} from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { Routes } from "../../root/routes";
import {
  USA_FLAG_ICON,
  EEA_UK_FLAG_ICON,
  CANADA_FLAG_ICON,
} from "../data-map/vendor-documentation/vendorDocumentationEditorUi";
import {
  getRegionGroupsForRequestTypes,
  getUSStateCodesForRequestTypes,
} from "../../utils/locationUtils";
import { getRequestTypeDescription } from "../../util/requestTypeDescriptions";

type WorkflowInformationCardProps = {
  title: string;
  description: string;
  /** Request types for this workflow - used to derive applicable regions */
  requestTypes: RegulationRequestTypeDto[];
  /** Org feature flags - only show region flags when org covers that region */
  organization: OrganizationDto | null;
  dataStorageSettingsUrl?: string;
};

export function WorkflowInformationCard({
  title,
  description,
  requestTypes,
  organization,
  dataStorageSettingsUrl = Routes.workflows.dataStorageSettings,
}: WorkflowInformationCardProps) {
  const DATA_STORAGE_SETTINGS_TEXT = "Data Storage settings";
  const descriptionParts = description.split(DATA_STORAGE_SETTINGS_TEXT);
  const hasDataStorageSettingsLink = descriptionParts.length > 1;

  const regionGroups = getRegionGroupsForRequestTypes(requestTypes);
  const usStateCodes = getUSStateCodesForRequestTypes(requestTypes);

  const showUS = regionGroups.includes(RegionGroup.UNITED_STATES);
  const showEeaUk = regionGroups.includes(RegionGroup.EEA_UK) && organization?.featureGdpr;
  const showCanada = regionGroups.includes(RegionGroup.CANADA) && organization?.featurePipeda;

  const hasAnyRegion = showUS || showEeaUk || showCanada;
  const usSupportLabel =
    usStateCodes.length > 0
      ? `Supported US states: ${usStateCodes.join(", ")}`
      : "Supported region: United States";

  return (
    <div className="rounded-lg border border-neutral-300 bg-white p-6 shadow-sm mb-6">
      <h3 className="text-lg font-semibold text-neutral-900 mb-2">{title}</h3>
      <p className="text-sm text-neutral-600 whitespace-pre-line mb-4">
        {hasDataStorageSettingsLink ? (
          <>
            {descriptionParts[0]}
            <a
              href={dataStorageSettingsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 underline hover:text-primary-700"
            >
              {DATA_STORAGE_SETTINGS_TEXT}
            </a>
            {descriptionParts.slice(1).join(DATA_STORAGE_SETTINGS_TEXT)}
          </>
        ) : (
          description
        )}
      </p>
      {hasAnyRegion && (
        <div className="flex gap-2 items-center" style={{ lineHeight: 0 }}>
          {showUS && (
            <ToolTip
              text={
                usStateCodes.length > 0 ? (
                  <div className="flex flex-col gap-0.5">
                    {usStateCodes.map((code) => (
                      <span key={code}>{code}</span>
                    ))}
                  </div>
                ) : (
                  <span>United States</span>
                )
              }
              placement="below"
              inline
              topLevelStyles={{ lineHeight: 0 }}
            >
              <button
                type="button"
                aria-label={usSupportLabel}
                className="inline-flex items-center justify-center w-[21px] h-4 [&>svg]:block cursor-help [&>svg]:translate-y-px"
              >
                {USA_FLAG_ICON}
              </button>
            </ToolTip>
          )}
          {showEeaUk && (
            <span
              role="img"
              aria-label="EEA and UK"
              className="inline-flex items-center justify-center w-[21px] h-4 [&>svg]:block"
            >
              {EEA_UK_FLAG_ICON}
            </span>
          )}
          {showCanada && (
            <span
              role="img"
              aria-label="Canada"
              className="inline-flex items-center justify-center w-[21px] h-4 [&>svg]:block"
            >
              {CANADA_FLAG_ICON}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const WORKFLOW_TITLES: Partial<Record<WorkflowType, string>> = {
  [WorkflowType.KNOW]: "Access Request",
  [WorkflowType.DELETE]: "Request to Delete",
  [WorkflowType.CORRECTION]: "Correction",
  [WorkflowType.APPEAL]: "Request to Appeal",
  [WorkflowType.WITHDRAW_CONSENT]: "Withdrawal of Consent",
  [WorkflowType.PROFILING_INFORMATION]: "Profiling Information",
  [WorkflowType.PORTABILITY]: "Portability",
  [WorkflowType.OBJECTION]: "Objection",
  [WorkflowType.RESTRICT_PROCESSING]: "Restrict Processing",
  [WorkflowType.OPT_OUT]: "Request to Opt-Out",
  [WorkflowType.LIMIT]: "Request to Limit",
  [WorkflowType.PROFILING_OPT_OUT]: "Profiling Opt-Out",
};

type WorkflowHelpCardProps = {
  workflowType: WorkflowType;
  requestTypes: RegulationRequestTypeDto[];
  organization: OrganizationDto | null;
  dataStorageSettingsUrl?: string;
};

export function WorkflowHelpCard({
  workflowType,
  requestTypes,
  organization,
  dataStorageSettingsUrl,
}: WorkflowHelpCardProps) {
  const title = WORKFLOW_TITLES[workflowType];
  if (!title || requestTypes.length === 0) {
    return null;
  }

  return (
    <WorkflowInformationCard
      title={title}
      description={getRequestTypeDescription(workflowType)}
      requestTypes={requestTypes}
      organization={organization}
      dataStorageSettingsUrl={dataStorageSettingsUrl}
    />
  );
}
